import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

import utils from "@utils";
import { ContactService } from "@services";
import { SelectBadges } from "../../../components";

import "./ClientCoordinators.scss";

const ClientCoordinators = props => {
	const { lang } = global;

	const { user, ClientCoordinatorIds, onClientCoordinatorIdsChange } = props;

	const {
		Organization: { Id },
		Id: userId,
		IsInternalCoordinator,
	} = user;

	const [clientCoordinators, setClientCoordinators] = useState([]);
	const [changeCoordinators, setChangeCoordinators] = useState(false);

	const loadClientCoordinators = useCallback(async () => {
		try {
			const {
				data: { ItemList },
			} = await ContactService.getAll({ clientId: Id, isOrganization: true });

			const contacts = ItemList.map(({ UserId: value, ContactName: label }) => ({
				value,
				label,
			})).filter(item => item.value !== 0 || !!item.value);

			const currentUser = contacts.find(item => item.value === userId);
			const clientCoordinatorIds =
				currentUser && IsInternalCoordinator
					? [currentUser]
					: ClientCoordinatorIds.map(id => {
							if (contacts.some(item => id.value === item.value)) {
								return contacts.find(item => item.value === id.value);
							}
							return { ...id, label: `user-${id.value}` };
						});

			onClientCoordinatorIdsChange(clientCoordinatorIds);
			setClientCoordinators(contacts);
		} catch {
			utils.notify({
				type: "error",
				message: lang.t("Error loading client coordinators"),
			});
		}
	});

	const addClientCoordinator = ({ target: { value } }) => {
		const addedClientCoordinator = clientCoordinators.find(
			item => item.value.toString() === value,
		);
		const clientCoordinatorIds = [...ClientCoordinatorIds, addedClientCoordinator];
		onClientCoordinatorIdsChange(clientCoordinatorIds);
	};

	const removeClientCoordinator = value => {
		const clientCoordinatorIds = ClientCoordinatorIds.filter(item => item.value !== value);
		onClientCoordinatorIdsChange(clientCoordinatorIds);
		setChangeCoordinators(clientCoordinatorIds.length !== 0);
	};

	const onChangeCoordinators = () => setChangeCoordinators(!changeCoordinators);

	useEffect(() => {
		if (!userId) return;

		loadClientCoordinators();
	}, [userId]);

	return (
		<div className="client-coordinators-input">
			<Choose>
				<When condition={ClientCoordinatorIds.length > 0 || changeCoordinators}>
					<Choose>
						<When condition={changeCoordinators}>
							<SelectBadges
								defaultOption={lang.t("Add Coordinator")}
								optionList={clientCoordinators}
								storedOptionList={ClientCoordinatorIds}
								handleOnChange={addClientCoordinator}
								handleOnDelete={removeClientCoordinator}
								editDone={() => setChangeCoordinators(false)}
								editPermission={user.IsStaffAdmin}
							/>
						</When>
						<Otherwise>
							<For
								each="item"
								of={ClientCoordinatorIds}
								index="index"
							>
								<span
									className="badge coordinators-badge"
									key={item.value}
								>
									{item.label}
								</span>
							</For>
							<button
								type="button"
								className="btn btn-link"
								onClick={onChangeCoordinators}
							>
								<i className="glyphicon glyphicon-pencil" />
								&nbsp;
								{lang.t("Edit")}
							</button>
						</Otherwise>
					</Choose>
				</When>
				<Otherwise>
					<span className="badge coordinators-badge">
						{lang.t("All (Agency Client)")}
					</span>
					<button
						type="button"
						className={!changeCoordinators ? "btn btn-link" : "hidden"}
						onClick={onChangeCoordinators}
					>
						<i className="glyphicon glyphicon-pencil" />
						&nbsp;
						{lang.t("Change")}
					</button>
				</Otherwise>
			</Choose>
		</div>
	);
};

ClientCoordinators.propTypes = {
	user: PropTypes.object,
	ClientCoordinatorIds: PropTypes.array.isRequired,
	onClientCoordinatorIdsChange: PropTypes.func.isRequired,
};

ClientCoordinators.defaultProps = {
	user: {},
};

export default ClientCoordinators;
